import {Icon} from '../ui/Icon';
import {useEffect,useLayoutEffect,useId,useRef,useState} from 'react';
import type {ComponentPropsWithoutRef} from 'react';
import {dateOnlyInputValue,parseDateOnlyInput} from '../dates';

type DateOnlyInputProps=Omit<ComponentPropsWithoutRef<'input'>,
  'type'|'name'|'value'|'defaultValue'|'onChange'|'inputMode'|'pattern'|'maxLength'>&{
  name?:string;
  value?:string|null|undefined;
  defaultValue?:string|null|undefined;
  onChange?:(canonicalValue:string)=>void;
};

function validationMessage(value:string){
  if(!value.trim())return'';
  try{parseDateOnlyInput(value);return''}catch{return'Enter a real date as DD/MM/YYYY'}
}

export function DateOnlyInput({name,value,defaultValue,onChange,required=false,disabled=false,...inputProps}:DateOnlyInputProps){
  const controlled=value!==undefined;
  const[displayValue,setDisplayValue]=useState(()=>dateOnlyInputValue(value??defaultValue));
  const input=useRef<HTMLInputElement>(null);
  const picker=useRef<HTMLInputElement>(null);
  const errorId=useId();
  const [implicitLabel,setImplicitLabel]=useState<string>();
  // Keep the text field's native label independent of its adjacent calendar button.
  // A parent can change its label in place, such as bank response -> actual opening date.
  useLayoutEffect(()=>{
    if(inputProps['aria-label']||inputProps['aria-labelledby'])return;
    const labels=Array.from(input.current?.labels??[]).map(label=>{
      const copy=label.cloneNode(true) as HTMLElement;
      copy.querySelectorAll('.date-only-control,.date-only-error').forEach(node=>node.remove());
      return copy.textContent?.trim();
    }).filter(Boolean).join(' ');
    if(labels&&labels!==implicitLabel)setImplicitLabel(labels);
  });
  const error=validationMessage(displayValue);
  const describedBy=[inputProps['aria-describedby'],error&&errorId].filter(Boolean).join(' ')||undefined;
  let canonicalValue='';
  if(!error)canonicalValue=parseDateOnlyInput(displayValue)??'';

  useEffect(()=>{if(controlled)setDisplayValue(dateOnlyInputValue(value))},[controlled,value]);
  useEffect(()=>input.current?.setCustomValidity(error),[error]);
  useEffect(()=>{
    const form=input.current?.form;
    if(controlled||!form)return;
    const reset=()=>setDisplayValue(dateOnlyInputValue(defaultValue));
    form.addEventListener('reset',reset);
    return()=>form.removeEventListener('reset',reset);
  },[controlled,defaultValue]);

  return <>
    <span className="date-only-control">
    <input {...inputProps} ref={input} type="text" inputMode="text" placeholder="DD/MM/YYYY"
      pattern="[0-9]{2}/[0-9]{2}/[0-9]{4}" maxLength={10} autoComplete="off"
      aria-label={inputProps['aria-label']??(inputProps['aria-labelledby']?undefined:implicitLabel)}
      value={displayValue} required={required} disabled={disabled} aria-invalid={Boolean(error)}
      aria-describedby={describedBy}
      onChange={event=>{
        const next=event.target.value,nextError=validationMessage(next);
        event.currentTarget.setCustomValidity(nextError);setDisplayValue(next);
        if(!nextError)onChange?.(parseDateOnlyInput(next)??'');
      }}/>
    <button type="button" className="date-only-picker" disabled={disabled||inputProps.readOnly} aria-label="Choose date" onClick={event=>{event.preventDefault();try{picker.current?.showPicker()}catch{input.current?.focus()}}}><Icon name="calendar-days" size={20}/></button>
    <input ref={picker} type="date" className="date-only-native" aria-hidden="true" aria-label="Calendar date" tabIndex={-1} disabled={disabled||inputProps.readOnly} value={canonicalValue} onChange={event=>{
      const next=event.currentTarget.value;if(!next)return;
      setDisplayValue(dateOnlyInputValue(next));input.current?.setCustomValidity('');onChange?.(next);
    }}/></span>
    {name&&<input type="hidden" name={name} value={canonicalValue} disabled={disabled}/>}
    {error&&<span id={errorId} className="date-only-error" role="alert">{error}</span>}
  </>;
}
